import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/config/env';
import type { ApiError, ApiResponse, RequestConfig } from './types';
import { TokenService } from '@/services/auth/tokenService';
import { TokenStorage } from '@/services/auth/tokenStorage';
import { silentRequest } from '@/config/msal.config';
import { msalInstance } from '@/config/msal.instance';
import { useSessionStore } from '@/stores/session.store';

// Create base axios instance
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: env.VITE_API_URL,
    timeout: env.VITE_API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      // Add X-Origin-Verify header if secret is configured
      ...(env.VITE_ORIGIN_VERIFY_SECRET && {
        'X-Origin-Verify': env.VITE_ORIGIN_VERIFY_SECRET,
      }),
    },
  });

  // Request interceptor for auth token
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig & { skipAuth?: boolean }) => {
      // Skip auth for certain endpoints
      if (config.skipAuth) {
        return config;
      }

      // Proactively refresh token if it's about to expire
      await TokenService.refreshIfNeeded();

      // Get valid token from TokenStorage
      const token = TokenStorage.getAccessToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for token refresh and error handling
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      return response;
    },
    async error => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // Handle 401/403 errors (MPN token expired, invalid, or revoked)
      // Backend may return 403 when the access token is expired/revoked
      // Skip retry for auth endpoints to prevent infinite loops
      const isAuthEndpoint =
        originalRequest.url?.includes('/login') ||
        originalRequest.url?.includes('/auth/');
      // A 403 with an errorCode is the backend refusing the request (e.g.
      // FORBIDDEN for a missing permission), not an expired session: API
      // Gateway's own 401/403 never carry one. Pass it on without a refresh.
      const isRefusal =
        status === 403 && Boolean(error.response?.data?.errorCode);
      if (
        (status === 401 || status === 403) &&
        !isRefusal &&
        !originalRequest._retry &&
        !isAuthEndpoint
      ) {
        originalRequest._retry = true;

        try {
          // Step 1: Try MPN token refresh (uses refresh token)
          const newToken = await TokenService.refreshAccessToken();

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client(originalRequest);
          }

          // Step 2: MPN refresh failed (refresh token expired).
          // Try to re-exchange: get MSAL ID token → exchange for new MPN tokens.
          // The backend POST /login accepts an idToken, not an MSAL access token.
          const account = msalInstance.getActiveAccount();
          if (account) {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              ...silentRequest,
              account,
            });

            if (tokenResponse.idToken) {
              // Dynamic import to avoid circular dependency
              const { authService } = await import(
                '@/services/auth/authService'
              );
              const mpnResponse = await authService.exchangeMicrosoftToken(
                tokenResponse.idToken
              );

              if (mpnResponse.accessToken) {
                // Also update legacy storage
                localStorage.setItem(
                  'mpn_access_token',
                  mpnResponse.accessToken
                );
                if (mpnResponse.refreshToken) {
                  localStorage.setItem(
                    'mpn_refresh_token',
                    mpnResponse.refreshToken
                  );
                }

                originalRequest.headers.Authorization = `Bearer ${mpnResponse.accessToken}`;
                return client(originalRequest);
              }
            }
          }

          // Step 3: All recovery failed
          throw new Error('Failed to acquire new MPN access token');
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);

          // Show session expired modal instead of hard-redirecting.
          // This keeps the user on the current page so they don't lose
          // context (form data, scroll position, etc.).
          useSessionStore.getState().setSessionExpired(true);

          return Promise.reject(refreshError);
        }
      }

      // Transform error response
      const apiError: ApiError = {
        message:
          error.response?.data?.message ||
          error.message ||
          'An unexpected error occurred',
        code:
          error.response?.data?.errorCode ||
          error.response?.data?.error_code ||
          error.response?.data?.code,
        status: error.response?.status,
        details: error.response?.data?.details,
      };

      return Promise.reject(apiError);
    }
  );

  return client;
};

export const apiClient = createApiClient();

// Helper function for making requests with custom config
export async function makeRequest<T = unknown>(
  config: AxiosRequestConfig & RequestConfig
): Promise<AxiosResponse<ApiResponse<T>>> {
  return apiClient.request(config);
}

// Convenience methods
export const api = {
  get: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig & RequestConfig
  ) => makeRequest<T>({ ...config, method: 'GET', url }),

  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & RequestConfig
  ) => makeRequest<T>({ ...config, method: 'POST', url, data }),

  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & RequestConfig
  ) => makeRequest<T>({ ...config, method: 'PUT', url, data }),

  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & RequestConfig
  ) => makeRequest<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig & RequestConfig
  ) => makeRequest<T>({ ...config, method: 'DELETE', url }),
};

export default apiClient;
